import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle2, Package, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(data);
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          // Continue polling
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('pending');
          setPaymentData(data);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-[#0F0720] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="glass-card p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-purple-400 mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-2">Processing Payment...</h1>
              <p className="text-purple-300">Please wait while we confirm your order.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="checkmark mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
              <p className="text-purple-300 mb-6">
                Thank you for your purchase. You'll receive a confirmation email shortly.
              </p>
              
              {paymentData && (
                <div className="bg-purple-500/10 rounded-xl p-4 mb-6 text-left">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="w-5 h-5 text-purple-400" />
                    <span className="text-white font-semibold">Order Details</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-purple-300">Amount:</span>
                      <span className="text-white mono">
                        ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-300">Status:</span>
                      <span className="text-green-400 font-semibold">Paid</span>
                    </div>
                  </div>
                </div>
              )}
              
              <Link to="/">
                <Button className="btn-primary w-full" data-testid="back-home-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </>
          )}

          {status === 'pending' && (
            <>
              <Loader2 className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-white mb-2">Payment Processing</h1>
              <p className="text-purple-300 mb-6">
                Your payment is being processed. Please check your email for confirmation.
              </p>
              <Link to="/">
                <Button className="btn-secondary w-full" data-testid="back-home-pending-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">⏱️</span>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Session Expired</h1>
              <p className="text-purple-300 mb-6">
                Your payment session has expired. Please try again.
              </p>
              <Link to="/">
                <Button className="btn-primary w-full" data-testid="try-again-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">❌</span>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Something Went Wrong</h1>
              <p className="text-purple-300 mb-6">
                We couldn't verify your payment. Please contact support if you were charged.
              </p>
              <Link to="/">
                <Button className="btn-secondary w-full" data-testid="back-home-error-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
